import { Button } from '@digdir/designsystemet-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldValues, useFormContext } from 'react-hook-form';
import { ChevronLeftIcon } from '@navikt/aksel-icons';

import { UnsavedChangesDialog } from './unsavedChangesDialog';

export type SubmitPayload<T> = {
  data: T;
  onSuccess: () => void;
};

interface Props<T> {
  isLoading: boolean;
  onSuccess: () => void;
  onSubmit: (payload: SubmitPayload<T>) => void;
}

// Form back button component - takes form data type T as generic
// Handles three scenarios on back button click:
// - Submits the form if there are valid changes
// - Shows a dialog with option to discard changes if there are errors
// - Navigates back directly if no changes

export const FormBackButton = <T extends FieldValues>({ isLoading, onSuccess, onSubmit }: Props<T>) => {
  const { t } = useTranslation('', { keyPrefix: 'form' });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const methods = useFormContext<T>();

  // Subscribe to all form fields to track changes
  methods.watch();

  const onBackClick = async () => {
    // Trigger form validation to know if data is valid
    const isValid = await methods.trigger();
    const hasChanges = Object.entries(methods.formState.dirtyFields).length > 0;

    // Form is not changed - we run success callback directly
    if (!hasChanges) {
      onSuccess();
      return;
    }

    // Form is invalid - show the discard changes dialog
    if (!isValid) {
      setIsDialogOpen(true);
      return;
    }

    // Form has valid changes - submit data and run success callback on completion
    onSubmit({ data: methods.getValues(), onSuccess });
  };

  return (
    <>
      <Button disabled={isLoading} variant="secondary" onClick={onBackClick}>
        <ChevronLeftIcon /> {t('common.previous')}
      </Button>
      <UnsavedChangesDialog onCancel={() => setIsDialogOpen(false)} isOpen={isDialogOpen} onConfirm={onSuccess} />
    </>
  );
};
